import { useWindowSize } from '@hooks';
import { NavItem } from '@staging-design-system';
import { DropdownWithMenuItemsAndIconTrigger } from '@ui/dropdowns';
import { CircularLoader } from '@ui/loaders';
import { generateKey, getClassName } from '@utils';
import type { MouseEvent, ReactNode } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Styled from './Menu.styled';

export interface MenuGroupItem {
  active?: boolean;
  title?: string;
  id?: string;
  actionList?: {
    label: string;
    value: string;
    selected: boolean;
    onClick: (groupItem: MenuGroupItem, deleteItem?: () => void) => void;
  }[];
}

export interface MenuItem<T extends MenuGroupItem> {
  active?: boolean;
  activeGroupItemId?: string;
  icon: ReactNode;
  hidden?: boolean;
  label: string;
  groups?: ReadonlyArray<{
    title?: string;
    items?: ReadonlyArray<T>;
  }>;
  pathname?: string;
  onClickGroupItem: (groupItem: T, event?: MouseEvent<HTMLElement>) => void;
  onLazyLoading?: (groupItemElement: Element) => Promise<{ hasMore: boolean }>;
}

interface Props<T extends MenuGroupItem> {
  isExpanded?: boolean;
  items: readonly MenuItem<T>[];
  onClickMenuItem: (
    menuItem: MenuItem<T>,
    event: MouseEvent<HTMLElement>
  ) => void;
}

function Menu<T extends MenuGroupItem = MenuGroupItem>(props: Props<T>) {
  const initActiveIndex = props.items.findIndex((item) => item.active);
  const [activeIndex, setActiveIndex] = useState<number>(initActiveIndex);
  const [deletedItems, setDeletedItems] = useState<string[]>([]);
  const touchStartedRef = useRef(false);
  const groupListRef = useRef<HTMLOListElement[]>([]);
  const windowSize = useWindowSize();
  const [isLazyLoading, setIsLazyLoading] = useState(false);
  const [hasMoreItems, setHasMoreItems] = useState(true);

  const menuItems: readonly MenuItem<T>[] = useMemo(() => {
    return props.items.map((option, index) => ({
      ...option,
      active: index === activeIndex
    }));
  }, [props.items, activeIndex]);

  const hasLazyLoading = useCallback(
    (index: number) => props.items[index].onLazyLoading !== undefined,
    [props.items]
  );

  const handleClickMenuItem = (
    itemIndex: number,
    event: MouseEvent<HTMLElement>
  ) => {
    const target = event.target;

    if (
      target instanceof Element &&
      target.closest('.menu-item-link')
    ) {
      return;
    }

    setActiveIndex((currentActiveIndex) => {
      return itemIndex === currentActiveIndex ? -1 : itemIndex;
    });

    props.onClickMenuItem(props.items[itemIndex], event);
  };

  useEffect(() => {
    if (!hasMoreItems) {
      setIsLazyLoading(false);
      return;
    }

    const observerList: IntersectionObserver[] = [];

    // Loop through each menu item that has lazy loading
    for (let index = 0; index < props.items.length; index++) {
      if (groupListRef.current[index] && hasLazyLoading(index)) {
        const callback: IntersectionObserverCallback = ([entry]) => {
          if (entry.isIntersecting) {
            // Show loading spinner
            setIsLazyLoading(true);

            // Call the lazy loading function to fetch more items
            props.items[index]
              .onLazyLoading(groupListRef.current[index])
              .then(({ hasMore }) => {
                setHasMoreItems(hasMore);
              });
          }
        };
        observerList[index] = new IntersectionObserver(callback, {
          root: groupListRef.current[index],
          threshold: 0.1
        });

        // Find the target element to observe
        const thresholdElement = groupListRef.current[index].querySelector(
          '.lazy-loading-threshold'
        );

        if (thresholdElement) {
          observerList[index].observe(thresholdElement);
        }
      }
    }

    // Cleanup function to unobserve all observers
    return () => {
      for (const observer of observerList) {
        observer?.disconnect();
      }
    };
  }, [hasLazyLoading, props.items, hasMoreItems]);

  return (
    <Styled.Menu isOpen={props.isExpanded}>
      {menuItems.map((menuItem, menuIndex) => (
        <Styled.MenuItem
          active={activeIndex === menuIndex && props.isExpanded}
          hidden={menuItem.hidden}
          isOpen={props.isExpanded}
          key={generateKey(`${menuItem.label}-${menuIndex}`)}>
          <NavItem
            data-testid={`sidepanel-${menuItem.label}`}
            expandable={Boolean(menuItem.groups)}
            icon={menuItem.icon}
            isExpanded={activeIndex === menuIndex && props.isExpanded}
            label={menuItem.label}
            selected={activeIndex === menuIndex && props.isExpanded}
            showLabel={props.isExpanded}
            onClick={(event) => handleClickMenuItem(menuIndex, event)}
          />
          {menuItem.groups && (
            <Styled.GroupList
              data-testid={`sidepanel-list-${menuItem.label}`}
              ref={(element) => {
                groupListRef.current[menuIndex] = element;
              }}
              onTouchMove={(event) => {
                event.currentTarget.classList.add('disable-active');
              }}
              onTouchEnd={(event) => {
                if (
                  event.target instanceof HTMLElement &&
                  event.target.classList.contains('action-menu')
                ) {
                  return;
                }

                if (event.currentTarget.classList.contains('disable-active')) {
                  // Ensure that the event target ref is available
                  // by passing it to an anonymous function, despite
                  // garbage collection
                  ((element: HTMLElement) =>
                    setTimeout(
                      () => element.classList.remove('disable-active'),
                      50
                    ))(event.currentTarget);
                  return;
                }

                if ('dataset' in event.target) {
                  const groupIndex = Number(event.target.dataset['groupIndex']);
                  const index = Number(event.target.dataset['groupItemIndex']);
                  const groupItem = menuItem.groups[groupIndex]?.items[index];

                  if (groupItem) {
                    // Add a brief delay to ensure the 'active' effect is visible
                    window.setTimeout(
                      () => menuItem.onClickGroupItem(groupItem),
                      50
                    );
                  }
                }
              }}>
              {menuItem.groups.map((group, groupIndex) => (
                <Styled.GroupListItem
                  aria-labelledby={`grp-li-${groupIndex + 1}`}
                  key={generateKey(`${group.title}-${groupIndex}`)}>
                  {group.title && group.items && group.items.length > 0 && (
                    <Styled.GroupTitle
                      id={`grp-li-${groupIndex + 1}`}
                      onClick={(event) => event.stopPropagation()}>
                      {group.title}
                    </Styled.GroupTitle>
                  )}
                  <Styled.Group>
                    {group.items?.map((groupItem, groupItemIndex) => {
                      const isActive =
                        groupItem.id === menuItem.activeGroupItemId;

                      const handleClickGroupItem = (
                        event: MouseEvent<HTMLElement>
                      ) => {
                        if (touchStartedRef.current) {
                          touchStartedRef.current = false;
                        } else if (!isActive) {
                          menuItem.onClickGroupItem(groupItem, event);
                        }
                      };

                      return (
                        <Styled.GroupItem
                          className={getClassName({
                            ['lazy-loading-threshold']:
                              groupIndex + 1 === menuItem.groups.length &&
                              groupItemIndex + 1 === group.items.length,
                            active: isActive && !windowSize.isLarge,
                            deleted: deletedItems.includes(groupItem.id)
                          })}
                          active={isActive}
                          key={generateKey(
                            `${groupItem.id}-${groupIndex}-${groupItemIndex}`
                          )}
                          tabIndex={0}
                          onTouchStart={() => {
                            touchStartedRef.current = true;
                          }}
                          onClick={handleClickGroupItem}>
                          {menuItem.pathname && (
                            <Styled.GroupItemLink
                              className={getClassName({
                                ['menu-item-link']: true
                              })}
                              data-group-index={groupIndex}
                              data-group-item-index={groupItemIndex}
                              href={`${menuItem.pathname}/${groupItem.id}`}
                              title={groupItem.title}
                              onClick={(event) => event.preventDefault()}
                            />
                          )}
                          <Styled.GroupItemText>
                            {groupItem.title}
                          </Styled.GroupItemText>
                          {groupItem.actionList && (
                            <Styled.ActionMenuWrapper>
                              <DropdownWithMenuItemsAndIconTrigger
                                iconTriggerClassName="action-menu"
                                iconTriggerActiveClassName="action-menu-active"
                                menuProps={{
                                  PopoverClasses: {
                                    paper: 'action-menu-paper'
                                  }
                                }}
                                options={groupItem.actionList.map((item) => ({
                                  ...item,
                                  onClick: () =>
                                    item.onClick(groupItem, () =>
                                      setDeletedItems([
                                        ...deletedItems,
                                        groupItem.id
                                      ])
                                    )
                                }))}
                              />
                            </Styled.ActionMenuWrapper>
                          )}
                        </Styled.GroupItem>
                      );
                    })}
                  </Styled.Group>
                </Styled.GroupListItem>
              ))}
              {hasLazyLoading(menuIndex) && hasMoreItems && (
                <Styled.Loading hidden={!isLazyLoading}>
                  <CircularLoader selectedSize="XS" />
                </Styled.Loading>
              )}
            </Styled.GroupList>
          )}
        </Styled.MenuItem>
      ))}
    </Styled.Menu>
  );
}

export default memo(Menu);
